/**
 * Condition DSL <-> visual builder.
 *
 * The API stores the DSL exactly as documented in docs/02 section 3.5; the
 * console edits a tree of builder nodes. Both directions are pure functions so
 * the round trip is testable: `builderToCondition(conditionToBuilder(c))`
 * deep-equals `c` for every condition the validator accepts.
 */
import {
  CONDITION_FIELDS,
  CONDITION_OPERATORS,
  isAllGroup,
  isAnyGroup,
  isFieldCondition,
  isNotGroup,
  validateCondition,
  type Condition,
  type ConditionError,
  type ConditionOperator,
} from '@vam/schema';
import { safeString } from './format.js';

export type Combinator = 'all' | 'any' | 'not';

export interface BuilderCondition {
  id: string;
  kind: 'condition';
  field: string;
  op: ConditionOperator;
  value?: unknown;
  ci?: boolean;
}

export interface BuilderGroup {
  id: string;
  kind: 'group';
  combinator: Combinator;
  children: BuilderNode[];
}

export type BuilderNode = BuilderCondition | BuilderGroup;

/** Operators that take no value at all. */
export const UNARY_OPERATORS: ConditionOperator[] = ['exists'];
/** Operators whose value is a list. */
export const LIST_OPERATORS: ConditionOperator[] = ['in', 'nin'];
/** Operators whose value is numeric or a comparable string. */
export const COMPARISON_OPERATORS: ConditionOperator[] = ['gt', 'lt'];
/** Operators for which a case-insensitive flag is meaningful. */
export const CI_OPERATORS: ConditionOperator[] = [
  'eq',
  'neq',
  'in',
  'nin',
  'contains',
  'starts_with',
  'ends_with',
  'regex',
];

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is one of',
  nin: 'is none of',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  regex: 'matches regex',
  gt: 'greater than',
  lt: 'less than',
  exists: 'exists',
};

export const FIELD_OPTIONS: readonly string[] = CONDITION_FIELDS;
export const OPERATOR_OPTIONS: readonly ConditionOperator[] = CONDITION_OPERATORS;

let counter = 0;
/** Stable-per-session node id; never serialised into the DSL. */
export function nodeId(prefix = 'n'): string {
  counter += 1;
  return `${prefix}${counter}`;
}

export function newCondition(field = 'severity', op: ConditionOperator = 'eq'): BuilderCondition {
  return { id: nodeId('c'), kind: 'condition', field, op, value: '' };
}

export function newGroup(combinator: Combinator = 'all', children?: BuilderNode[]): BuilderGroup {
  return { id: nodeId('g'), kind: 'group', combinator, children: children ?? [newCondition()] };
}

/** An empty root: an `all` group with one blank row. */
export function emptyBuilder(): BuilderGroup {
  return newGroup('all', [newCondition()]);
}

// --- DSL -> builder ----------------------------------------------------------

export function conditionToBuilder(condition: unknown): BuilderGroup {
  const node = toNode(condition);
  if (node.kind === 'group') return node;
  return { id: nodeId('g'), kind: 'group', combinator: 'all', children: [node] };
}

function toNode(condition: unknown): BuilderNode {
  if (typeof condition !== 'object' || condition === null) return newCondition();
  const c = condition as Condition;
  if (isAllGroup(c)) {
    return { id: nodeId('g'), kind: 'group', combinator: 'all', children: c.all.map(toNode) };
  }
  if (isAnyGroup(c)) {
    return { id: nodeId('g'), kind: 'group', combinator: 'any', children: c.any.map(toNode) };
  }
  if (isNotGroup(c)) {
    return { id: nodeId('g'), kind: 'group', combinator: 'not', children: [toNode(c.not)] };
  }
  if (isFieldCondition(c)) {
    const leaf: BuilderCondition = { id: nodeId('c'), kind: 'condition', field: c.field, op: c.op };
    if ('value' in c) leaf.value = c.value;
    if (c.ci !== undefined) leaf.ci = c.ci;
    return leaf;
  }
  return newCondition();
}

// --- builder -> DSL ----------------------------------------------------------

export function builderToCondition(node: BuilderNode): Condition {
  if (node.kind === 'condition') {
    const leaf: Record<string, unknown> = { field: node.field, op: node.op };
    if ('value' in node && node.value !== undefined) leaf.value = node.value;
    if (node.ci !== undefined) leaf.ci = node.ci;
    return leaf as unknown as Condition;
  }
  const children = node.children.map(builderToCondition);
  if (node.combinator === 'not') {
    const inner = children.length === 1 ? children[0]! : ({ all: children } as Condition);
    return { not: inner };
  }
  return node.combinator === 'all' ? { all: children } : { any: children };
}

// --- value text helpers ------------------------------------------------------

/** Render a DSL value in the single-line text input the builder shows. */
export function valueToText(value: unknown, op: ConditionOperator): string {
  if (UNARY_OPERATORS.includes(op)) return value === false ? 'false' : 'true';
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.map(safeString).join(', ');
  return safeString(value);
}

/** Parse the text input back into the typed value the operator expects. */
export function textToValue(text: string, op: ConditionOperator): unknown {
  if (UNARY_OPERATORS.includes(op)) return text.trim().toLowerCase() !== 'false';
  if (LIST_OPERATORS.includes(op)) {
    return text
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .map(coerceScalar);
  }
  if (COMPARISON_OPERATORS.includes(op)) {
    const trimmed = text.trim();
    const numeric = Number(trimmed);
    return trimmed !== '' && Number.isFinite(numeric) ? numeric : trimmed;
  }
  return text;
}

function coerceScalar(part: string): string | number | boolean {
  if (part === 'true') return true;
  if (part === 'false') return false;
  const numeric = Number(part);
  return part !== '' && Number.isFinite(numeric) && /^-?\d+(\.\d+)?$/.test(part) ? numeric : part;
}

/** Keep a leaf's value consistent when the operator changes under it. */
export function retypeValue(value: unknown, op: ConditionOperator): unknown {
  return textToValue(valueToText(value, op), op);
}

// --- validation --------------------------------------------------------------

export interface DslParseResult {
  ok: boolean;
  condition?: Condition;
  errors: ConditionError[];
}

/** Validate a condition tree with the shared validator from @vam/schema. */
export function validate(condition: unknown): DslParseResult {
  const result = validateCondition(condition);
  return result.ok
    ? { ok: true, condition: result.condition, errors: [] }
    : { ok: false, errors: result.errors };
}

/** Parse a DSL JSON document typed into the advanced editor. */
export function parseDslJson(text: string): DslParseResult & { syntaxError?: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, condition: { all: [] }, errors: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return {
      ok: false,
      errors: [],
      syntaxError: err instanceof Error ? err.message : 'invalid JSON',
    };
  }
  return validate(parsed);
}

/** Pretty JSON for the advanced editor. */
export function stringifyCondition(condition: unknown): string {
  return JSON.stringify(condition ?? { all: [] }, null, 2);
}

/** True when the tree carries no usable leaf (used to skip sending a filter). */
export function isEmptyCondition(condition: unknown): boolean {
  if (typeof condition !== 'object' || condition === null) return true;
  const c = condition as Condition;
  if (isAllGroup(c)) return c.all.length === 0 || c.all.every(isEmptyCondition);
  if (isAnyGroup(c)) return c.any.length === 0 || c.any.every(isEmptyCondition);
  if (isNotGroup(c)) return isEmptyCondition(c.not);
  if (isFieldCondition(c)) return !c.field;
  return true;
}

/** One-line human summary used on rule and policy cards. */
export function describeCondition(condition: unknown, depth = 0): string {
  if (typeof condition !== 'object' || condition === null) return 'anything';
  const c = condition as Condition;
  if (isAllGroup(c)) {
    if (c.all.length === 0) return 'anything';
    const parts = c.all.map((child) => describeCondition(child, depth + 1));
    return depth > 0 ? `(${parts.join(' and ')})` : parts.join(' and ');
  }
  if (isAnyGroup(c)) {
    if (c.any.length === 0) return 'nothing';
    const parts = c.any.map((child) => describeCondition(child, depth + 1));
    return depth > 0 ? `(${parts.join(' or ')})` : parts.join(' or ');
  }
  if (isNotGroup(c)) return `not ${describeCondition(c.not, depth + 1)}`;
  if (isFieldCondition(c)) {
    const label = OPERATOR_LABELS[c.op];
    if (UNARY_OPERATORS.includes(c.op)) return `${c.field} ${label}`;
    return `${c.field} ${label} ${valueToText(c.value, c.op)}`;
  }
  return 'anything';
}
